import { Link } from "react-router-dom";
import { ArrowUpRight, Boxes, Container, BadgeDollarSign, Clock, Ship, Package } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem, SectionLabel } from "../components/Motion";

const TERMS = [
  {
    icon: Boxes,
    title: "MOQ",
    body: [
      "Typical MOQ starts from 1 x 20FT container for bulk export orders.",
      "Actual MOQ depends on product, packaging and supplier requirements.",
    ],
  },
  {
    icon: Container,
    title: "Loading Capacity",
    body: [
      "Indicative loading capacity: approximately 18 MT per 20FT container and 26 MT per 40FT container.",
      "Actual loading capacity may vary depending on product type, packaging configuration and container loading requirements.",
    ],
  },
  {
    icon: BadgeDollarSign,
    title: "Payment Terms",
    body: [
      "100% T/T in advance \u2014 full payment by Telegraphic Transfer before order processing and production begins.",
      "50% T/T advance \u2014 50% advance by Telegraphic Transfer, with the remaining 50% paid before container stuffing.",
      "Final payment terms are confirmed based on product, order volume, and supplier arrangement.",
    ],
  },
  {
    icon: Clock,
    title: "Lead Time",
    body: [
      "Typical lead time: 3\u20134 weeks.",
      "Typical production and preparation lead time is approximately 3\u20134 weeks after order confirmation and required payment, subject to product specification, packaging requirements, order quantity and manufacturing schedule.",
    ],
  },
  {
    icon: Ship,
    title: "Incoterms & Shipping",
    body: [
      "FOB \u2014 Indonesia.",
      "CIF \u2014 upon request. CIF quotations can be arranged based on destination port and current freight rates.",
      "FOB and CIF quotations are available depending on product, destination port and current shipping conditions.",
    ],
  },
  {
    icon: Package,
    title: "Packaging",
    body: [
      "1 KG, 10 KG, bulk and custom packaging configurations.",
      "OEM / private-label packaging can be discussed per product and order quantity.",
      "Packaging configuration depends on product, buyer requirements and manufacturing partner.",
    ],
  },
];

export default function CommercialTerms() {
  return (
    <>
      <SEO />
      <div data-testid="commercial-terms-page" className="bg-offwhite pt-40 pb-24 sm:pb-32 min-h-screen">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
          <SectionLabel index="TERMS" title="Commercial Terms" />
          <Reveal className="mt-8 max-w-4xl">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
              Commercial Terms
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-charcoal/65 leading-relaxed">
              Clear, transparent commercial frameworks for international buyers. Final terms are always confirmed per
              product, order and manufacturing partner.
            </p>
          </Reveal>

          <Stagger className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.08}>
            {TERMS.map((term, i) => (
              <StaggerItem key={term.title}>
                <div
                  data-testid={`term-${term.title.toLowerCase().replace(/[\s/&]+/g, "-")}`}
                  className="bg-offwhite hover:bg-sand p-8 sm:p-10 h-full flex flex-col gap-6 transition-colors duration-300"
                >
                  <div className="flex items-center justify-between">
                    <term.icon className="w-7 h-7 text-forest" strokeWidth={1.5} />
                    <span className="font-display text-xs font-semibold tracking-[0.3em] text-charcoal/30">
                      0{i + 1}
                    </span>
                  </div>
                  <h2 className="font-display text-2xl font-bold tracking-tight">{term.title}</h2>
                  <div className="space-y-3">
                    {term.body.map((line) => (
                      <p key={line} className="text-sm text-charcoal/65 leading-relaxed flex gap-3">
                        <span className="w-1.5 h-1.5 rotate-45 bg-clay mt-[7px] shrink-0" />
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-20 bg-charcoal text-offwhite p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Let's talk about your supply.</h2>
              <p className="mt-3 text-sm text-offwhite/60 max-w-lg leading-relaxed">
                Tell us what you need. We'll review your requirements and identify suitable sourcing options from
                Indonesia.
              </p>
            </div>
            <Link
              to="/request-a-quote"
              data-testid="terms-quote-cta"
              className="group inline-flex items-center gap-3 bg-forest hover:bg-moss text-offwhite font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-300 shrink-0"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </div>
    </>
  );
}
